import React, { useMemo, useEffect } from 'react';
import { Appointment } from './AppointmentCard';
import AppointmentCard from './AppointmentCard';
import './SchedulingCalendar.css';

interface Professional {
    id: number;
    name: string;
    photo?: string;
    bio?: string;
    agendas?: any[];
}

interface WeekViewProps {
    date: Date;
    appointments: Appointment[];
    professionals: Professional[];
    onTimeSlotClick: (time: Date, professionalId: number) => void;
    onAppointmentClick: (appointment: Appointment) => void;
    holidays: any[];
    // deprecated props
    interval?: number;
    startHour?: number;
    endHour?: number;
}

const WeekView: React.FC<WeekViewProps> = ({
    date,
    appointments,
    professionals,
    holidays,
    onTimeSlotClick,
    onAppointmentClick
}) => {

    const slotHeight = 60; // px per slot

    // Calculate week days (Global)
    const weekDays = useMemo(() => {
        const curr = new Date(date);
        const day = curr.getDay();
        const diff = curr.getDate() - day; // Adjust to Sunday
        const monday = new Date(curr.setDate(diff));

        const days = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            days.push(d);
        }
        return days;
    }, [date]);

    // Current Time Indicator Logic (Global)
    const [currentTime, setCurrentTime] = React.useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const isSameDate = (d1: Date, d2: Date) => {
        return d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear();
    };

    const isToday = (d: Date) => {
        return isSameDate(d, new Date());
    };

    // Helper to get configuration for a specific professional
    const getProfessionalConfig = (prof: Professional) => {
        // Default
        let interval = 30;
        let startH = 8;
        let endH = 19;
        let currentAgenda: any = null;

        if (prof.agendas && prof.agendas.length > 0) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            const currentISO = `${y}-${m}-${d}`;

            currentAgenda = prof.agendas.find((a: any) => {
                const startValid = !a.start_date || a.start_date <= currentISO;
                const endValid = !a.end_date || a.end_date >= currentISO;
                return startValid && endValid;
            });

            if (currentAgenda) {
                if (currentAgenda.slot_duration) {
                    const parts = currentAgenda.slot_duration.split(':');
                    if (parts.length >= 2) {
                        const h = parseInt(parts[0], 10);
                        const min = parseInt(parts[1], 10);
                        const total = h * 60 + min;
                        if (total > 0) interval = total;
                    }
                }
                if (currentAgenda.start_time) {
                    const [h] = currentAgenda.start_time.split(':').map(Number);
                    if (!isNaN(h)) startH = h;
                }
                if (currentAgenda.end_time) {
                    const [h, m] = currentAgenda.end_time.split(':').map(Number);
                    let finalH = h;
                    if (m > 0) finalH = h + 1;
                    startH = startH < finalH ? startH : 8;
                    endH = finalH > startH ? finalH : 19;
                }
            }
        }
        return { interval, startH, endH, currentAgenda };
    };

    const renderProfessionalTable = (prof: Professional) => {
        const { interval, startH, endH, currentAgenda } = getProfessionalConfig(prof);

        // Generate slots
        const slots: Date[] = [];
        let current = new Date();
        current.setHours(startH, 0, 0, 0);
        const endTime = new Date();
        endTime.setHours(endH, 0, 0, 0);

        while (current < endTime) {
            slots.push(new Date(current));
            current.setMinutes(current.getMinutes() + interval);
        }

        const getAppointmentsForSlot = (day: Date, slot: Date, professionalId: number) => {
            const slotStartMs = new Date(day);
            slotStartMs.setHours(slot.getHours(), slot.getMinutes(), 0, 0);
            const slotStartVal = slotStartMs.getTime();
            const slotEndVal = slotStartVal + interval * 60 * 1000;

            return appointments.filter(a => {
                if (a.professional !== professionalId) return false;

                const appStart = new Date(a.start_time);
                if (!isSameDate(appStart, day)) return false;

                const startMs = appStart.getTime();
                return startMs >= slotStartVal && startMs < slotEndVal;
            });
        };

        const checkSlotBlocked = (day: Date, slot: Date) => {
            if (!currentAgenda) return { blocked: true, reason: 'Indisponível' };

            // 1. Weekday Check
            const weekDaysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const dayName = weekDaysMap[day.getDay()];
            if (currentAgenda[dayName] === false) {
                return { blocked: true, reason: 'Indisponível' };
            }

            // 2. Holiday Check
            const y = day.getFullYear();
            const m = String(day.getMonth() + 1).padStart(2, '0');
            const d = String(day.getDate()).padStart(2, '0');
            const dayISO = `${y}-${m}-${d}`;

            const isHoliday = holidays.find(h => h.date === dayISO);
            if (isHoliday && !currentAgenda.allow_holidays) {
                return { blocked: true, reason: 'Indisponível' };
            }

            // 3. Lunch Check
            if (currentAgenda.lunch_start_time && currentAgenda.lunch_end_time) {
                const slotTimeVal = slot.getHours() * 60 + slot.getMinutes();
                const [lsH, lsM] = currentAgenda.lunch_start_time.split(':').map(Number);
                const [leH, leM] = currentAgenda.lunch_end_time.split(':').map(Number);
                const lunchStartVal = lsH * 60 + lsM;
                const lunchEndVal = leH * 60 + leM;

                const slotEndVal = slotTimeVal + interval;

                // Overlap: SlotStart < LunchEnd AND SlotEnd > LunchStart
                if (slotTimeVal < lunchEndVal && slotEndVal > lunchStartVal) {
                    return { blocked: true, reason: 'Indisponível' };
                }
            }

            return { blocked: false, reason: '' };
        };

        const getAppointmentStyle = (appointment: Appointment, slotTime: Date): React.CSSProperties => {
            const start = new Date(appointment.start_time);
            const end = new Date(appointment.end_time);

            const startH = start.getHours();
            const startM = start.getMinutes();
            const endH = end.getHours();
            const endM = end.getMinutes();

            const durationMinutes = (endH * 60 + endM) - (startH * 60 + startM);
            const slotStartMinutes = slotTime.getHours() * 60 + slotTime.getMinutes();
            const appStartMinutes = startH * 60 + startM;

            const minutesFromSlotStart = Math.max(0, appStartMinutes - slotStartMinutes);
            const pxPerMinute = slotHeight / interval;

            const top = minutesFromSlotStart * pxPerMinute;
            const height = durationMinutes * pxPerMinute;

            return {
                top: `${top}px`,
                height: `${height}px`,
                position: 'absolute',
                width: '100%',
                left: '0',
                fontSize: '0.75rem',
                padding: '2px',
                zIndex: 10
            };
        };

        const handleSlotClick = (d: Date, slotTime: Date, professionalId: number) => {
            const slotDate = new Date(d);
            slotDate.setHours(slotTime.getHours(), slotTime.getMinutes(), 0, 0);
            onTimeSlotClick(slotDate, professionalId);
        };

        const calcCurrentTimeTop = () => {
            const currentH = currentTime.getHours();
            const currentM = currentTime.getMinutes();
            const totalMinutes = currentH * 60 + currentM;
            const startMinutes = startH * 60;
            const endMinutes = endH * 60;

            if (totalMinutes < startMinutes || totalMinutes > endMinutes) return null;

            const minutesFromStart = totalMinutes - startMinutes;
            const pxPerMinute = slotHeight / interval;

            return minutesFromStart * pxPerMinute;
        };

        const currentTimeTop = calcCurrentTimeTop();

        return (
            <div key={prof.id} className="d-flex flex-column border rounded shadow-sm bg-white week-view-prof-col" style={{ minWidth: '800px', flex: 1, marginRight: '1rem' }}>
                {/* Professional Header */}
                <div className="p-3 border-bottom d-flex align-items-center justify-content-center gap-2 bg-light rounded-top" style={{ height: '60px' }}>
                    <div className="professional-avatar" style={{ width: 32, height: 32 }}>
                        {prof.photo ? (
                            <img src={prof.photo} alt={prof.name} className="w-100 h-100 rounded-circle active-avatar" />
                        ) : (
                            prof.name.charAt(0).toUpperCase()
                        )}
                    </div>
                    <span className="fw-bold text-dark">{prof.name}</span>
                </div>

                {/* Table Logic */}
                <div className="position-relative flex-grow-1" style={{ overflowY: 'auto' }}>
                    {/* Current Time Line */}
                    {currentTimeTop !== null && (
                        <div
                            className="current-time-line"
                            style={{
                                top: `${currentTimeTop + 80}px`, // +80 for header (time/days row)
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                width: '100%',
                                zIndex: 60,
                                borderTop: '2px solid red',
                                pointerEvents: 'none'
                            }}
                        >
                            <div className="current-time-badge" style={{
                                position: 'absolute',
                                left: 0,
                                top: '-10px',
                                background: 'red',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '2px 4px',
                                borderRadius: '0 4px 4px 0'
                            }}>
                                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    )}

                    <table className="table table-bordered mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                        <thead className="sticky-top bg-white" style={{ top: 0, zIndex: 100 }}>
                            <tr>
                                <th className="sticky-start bg-white text-center align-middle border-bottom p-0 shadow-sm border-end"
                                    style={{ width: '80px', height: '80px', left: 0, zIndex: 101 }}>
                                    <div className="d-flex align-items-center justify-content-center h-100 fw-bold text-secondary">
                                        Hora
                                    </div>
                                </th>
                                {weekDays.map(day => (
                                    <th key={day.toISOString()}
                                        className={`text-center align-middle border-bottom border-end p-1 ${isToday(day) ? 'bg-light-primary' : 'bg-white'}`}
                                        style={{ minWidth: '100px', height: '80px', backgroundColor: isToday(day) ? '#e0f2fe' : 'white' }}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                            <span className={`fw-bold text-capitalize small ${isToday(day) ? 'text-primary' : 'text-dark'}`}>
                                                {day.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')}
                                            </span>
                                            <span className={`h6 mb-0 ${isToday(day) ? 'text-primary' : 'text-secondary'}`}>
                                                {day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                            </span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {slots.map((slot, index) => (
                                <tr key={index}>
                                    <td className="sticky-start bg-white text-center align-top p-0 border-end"
                                        style={{
                                            height: `${slotHeight}px`,
                                            left: 0,
                                            zIndex: 50,
                                            width: '80px',
                                            verticalAlign: 'top',
                                            borderBottom: '1px solid #eaecf0'
                                        }}>
                                        <span className="d-block text-muted small mt-1" style={{ fontSize: '0.70rem', fontWeight: 500 }}>
                                            {slot.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </td>
                                    {weekDays.map(day => {
                                        const { blocked, reason } = checkSlotBlocked(day, slot);
                                        return (
                                            <td key={day.toISOString()}
                                                className={`p-0 position-relative border-end ${blocked ? 'bg-light' : 'time-slot-hover'}`}
                                                style={{
                                                    height: `${slotHeight}px`,
                                                    verticalAlign: 'top',
                                                    borderBottom: '1px solid #eaecf0',
                                                    backgroundColor: blocked ? '#f3f4f6' : (isToday(day) ? 'rgba(240, 249, 255, 0.3)' : '#fafafa'),
                                                    cursor: blocked ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={() => !blocked && handleSlotClick(day, slot, prof.id)}>

                                                {blocked && (
                                                    <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted small user-select-none" style={{ fontSize: '0.65rem' }}>
                                                        {reason}
                                                    </div>
                                                )}

                                                {!blocked && getAppointmentsForSlot(day, slot, prof.id).map(appointment => (
                                                    <AppointmentCard
                                                        key={appointment.id}
                                                        appointment={appointment}
                                                        onClick={onAppointmentClick}
                                                        style={getAppointmentStyle(appointment, slot)}
                                                        showProfessional={false}
                                                    />
                                                ))}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="h-100 w-100 calendar-container-scroll bg-light p-3">
            <div className="d-flex flex-nowrap h-100" style={{ overflowX: 'auto', paddingBottom: '1rem' }}>
                {professionals.map(prof => renderProfessionalTable(prof))}
            </div>
        </div>
    );
};

export default WeekView;
